import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import Card from '../shared/Card';
import './StatsSection.css';

const StatsSection = ({ stats }) => {
  const defaultStats = [
    {
      id: 'users',
      number: '57M',
      label: 'Americans with Disabilities',
      description: 'People who could benefit from adaptive gaming solutions',
      icon: '👥'
    },
    {
      id: 'games',
      number: '15+',
      label: 'Therapeutic Games',
      description: 'Carefully designed games for different therapy goals',
      icon: '🎮'
    },
    {
      id: 'improvement',
      number: '85%',
      label: 'Patient Adherence',
      description: 'Increased engagement with therapy sessions',
      icon: '📈'
    },
    {
      id: 'devices',
      number: '3',
      label: 'Device Types Supported',
      description: 'Kinect, Orbbec Astra, and Leap Motion compatibility',
      icon: '🔗'
    }
  ];

  const statsToShow = stats || defaultStats;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const statVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="stats-section" aria-labelledby="stats-title">
      <div className="container">
        <motion.div
          className="stats-section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 id="stats-title" className="stats-section-title">
            Making a Real Impact
          </h2>
          <p className="section-subtitle">
            See how enAble Games is transforming adaptive recreation and therapy
          </p>
        </motion.div>

        <motion.div
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {statsToShow.map((stat, index) => (
            <motion.div
              key={stat.id}
              variants={statVariants}
              className="stat-item"
            >
              <Card
                variant="glass"
                hover={true}
                className="stat-card"
              >
                <div className="stat-icon" aria-hidden="true">
                  {stat.icon}
                </div>
                <div className="stat-content">
                  <div className="stat-number" aria-label={`${stat.number} ${stat.label}`}>
                    {stat.number}
                  </div>
                  <h3 className="stat-label">{stat.label}</h3>
                  <p className="stat-description">{stat.description}</p>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional Info Section */}
        <motion.div
          className="impact-highlight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <Card variant="gradient" className="impact-card">
            <div className="impact-content">
              <h3 className="impact-title">
                Proven Results in Therapeutic Gaming
              </h3>
              <p className="impact-description">
                Our adaptive gaming platform has been shown to significantly improve 
                patient engagement and therapy outcomes through personalized, accessible gameplay.
              </p>
              <div className="impact-features">
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <span>Automatic ROM adaptation</span>
                </div>
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <span>Real-time performance tracking</span>
                </div>
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <span>Remote provider monitoring</span>
                </div>
                <div className="feature-item">
                  <span className="feature-check">✓</span>
                  <span>Device-independent platform</span>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

StatsSection.propTypes = {
  stats: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    number: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired
  }))
};

export default StatsSection;